import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const PremsiTutorialButton = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [stepAnimations, setStepAnimations] = useState(Array(6).fill(false));
    const [learnMoreVisible, setLearnMoreVisible] = useState(false);
    const contentRef = useRef(null);
    const navigate = useNavigate()
    const steps = [
        {
            title: 'Just here to enjoy Premsi?',
            description: 'Create your Premsi account and follow your favorite creators. Support them on their journey.',
        },
        {
            title: 'Sign Up & Set Up Your Profile',
            description: 'Create your Premsi account. Complete your profile by adding a bio, profile picture and your paypal so we can pay you.',
        },
        {
            title: 'Choose What to Sell',
            description: 'Sell your digital products through the app, choose between photos, videos, PDFs, groups and soon much more.',
        },
        {
            title: 'Set Your Prices',
            description: 'Choose prices from 1$ to 500$ or monthly subscriptions for your digital products or groups.',
        },
        {
            title: 'Promote Your Products',
            description: 'Share your locked messages on all your socials. This step is crucial to get the maximum profit on Premsi.',
        },
        {
            title: 'Engage & Grow',
            description: 'Interact with fans through posts and messages. Analyze your sales data, expand your product range, and build lasting relationships to grow your business.',
        },
    ];

    const toggleTutorial = () => {
        if (!isExpanded) {
            setIsExpanded(true);
            // Animate steps in sequence
            steps.forEach((_, index) => {
                setTimeout(() => {
                    setStepAnimations(prev => {
                        const newAnimations = [...prev];
                        newAnimations[index] = true;
                        return newAnimations;
                    });
                }, 300 + (index * 100));
            });

            // Show learn more button last
            setTimeout(() => {
                setLearnMoreVisible(true);
            }, 300 + (steps.length * 100) + 300);
        } else {
            setIsExpanded(false);
            setStepAnimations(Array(6).fill(false));
            setLearnMoreVisible(false);
        }
    };

    const handleLearnMore = () => {
        window.open('https://www.premsi.com/explanation', '_blank');
    };

    return (
        <div className="tutorial-container">
            <div className="tutorial-wrapper">
                <button
                    className={`main-button ${isExpanded ? 'main-button-expanded' : ''}`}
                    onClick={toggleTutorial}
                >
                    <span className="button-text">Learn how to use Premsi</span>
                    <span className={`chevron ${isExpanded ? 'rotated' : ''}`}>▼</span>
                </button>

                <div className={`tutorial-content ${isExpanded ? 'expanded' : ''}`}>
                    <div className="steps-container">
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                {index === 0 && (
                                    <h3 className="section-title">For Fans</h3>
                                )}
                                {index === 1 && (
                                    <>
                                        <div className="or-divider">
                                            <div className="or-line"></div>
                                            <span className="or-text">OR</span>
                                            <div className="or-line"></div>
                                        </div>
                                        <h3 className="section-title">For Creators</h3>
                                    </>
                                )}

                                <div className={`step ${stepAnimations[index] ? 'animated' : ''}`}>
                                    <div className="step-number">
                                        <span>{index + 1}</span>
                                    </div>
                                    <div className="step-content">
                                        <h4 className="step-title">{step.title}</h4>
                                        <p className="step-description">{step.description}</p>
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}

                        <div className={`learn-more-container ${learnMoreVisible ? 'visible' : ''}`}>
                            <button
                                className="learn-more-button"
                                onClick={() => navigate("/download")}
                            >
                                Start with Premsi
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .tutorial-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 5rem;
                    padding: 2rem 1.5rem;
                    font-family: "Klarheit Grotesk Regular", sans-serif;
                }

                .tutorial-wrapper {
                    border-radius: 24px;
                    padding: 0;
                    width: 100%;
                    max-width: 700px;
                    box-shadow: 0 4px 24px rgba(0, 159, 253, 0.08), 0 20px 60px rgba(0, 159, 253, 0.12);
                    border: 1px solid rgba(0, 159, 253, 0.12);
                    background: #fff;
                    overflow: hidden;
                }

                .main-button {
                    width: 100%;
                    background: linear-gradient(135deg, #009ffd 0%, #00a8f0 40%, #00b8e6 70%, #00d4ff 100%);
                    border: none;
                    border-radius: 20px;
                    transition: border-radius 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
                    padding: 1.25rem 2rem;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    box-shadow: 0 4px 20px rgba(0, 159, 253, 0.3), 0 1px 0 rgba(255, 255, 255, 0.2) inset;
                    position: relative;
                    overflow: hidden;
                }

                .main-button::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
                    pointer-events: none;
                }

                .main-button:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 36px rgba(0, 159, 253, 0.4), 0 1px 0 rgba(255, 255, 255, 0.25) inset;
                }

                .main-button:active {
                    transform: translateY(0);
                }

                .main-button-expanded {
                    border-radius: 20px 20px 0 0;
                }

                .button-text {
                    color: white;
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin-right: 0.6rem;
                    letter-spacing: 0.02em;
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                }

                .chevron {
                    color: white;
                    font-size: 0.9rem;
                    font-weight: bold;
                    transition: transform 0.3s ease;
                }

                .chevron.rotated {
                    transform: rotate(180deg);
                }

                .tutorial-content {
                    background: linear-gradient(180deg, #f4fbff 0%, #fafcff 50%, #ffffff 100%);
                    border-radius: 0 0 24px 24px;
                    border: 1px solid rgba(0, 159, 253, 0.1);
                    border-top: none;
                    margin-top: 0;
                    overflow: hidden;
                    max-height: 0;
                    opacity: 0;
                    transform: scaleY(0);
                    transform-origin: top;
                    transition: all 0.4s ease;
                    box-shadow: 0 12px 40px rgba(0, 159, 253, 0.06);
                }

                .tutorial-content.expanded {
                    max-height: 2000px;
                    opacity: 1;
                    transform: scaleY(1);
                }

                .steps-container {
                    padding: 2.5rem 2.25rem 2.75rem;
                    max-width: 100%;
                }

                .section-title {
                    text-align: left;
                    width: 100%;
                    color: #0088dd;
                    font-family: "FSP DEMO - Universo Black", sans-serif;
                    font-weight: 700;
                    font-size: 1.4rem;
                    margin-bottom: 1.35rem;
                    margin-top: 0;
                    padding-bottom: 0.6rem;
                    display: inline-block;
                    text-decoration: none;
                    position: relative;
                    border: none;
                    letter-spacing: -0.01em;
                }

                .section-title::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    width: 100%;
                    height: 4px;
                    background: linear-gradient(90deg, #009ffd, #00b8e6);
                    border-radius: 2px;
                }

                .section-title:first-of-type {
                    margin-top: 0;
                }

                .section-title + .step {
                    margin-top: 0;
                    border-top: none;
                }

                .or-divider {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 2.25rem 0 1.5rem;
                    padding: 0 0.5rem;
                }

                .or-line {
                    flex: 1;
                    max-width: 140px;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(0, 159, 253, 0.2), rgba(0, 159, 253, 0.35), transparent);
                }

                .or-text {
                    margin: 0 1.25rem;
                    padding: 0.4rem 1.1rem;
                    color: #009ffd;
                    font-size: 0.75rem;
                    font-weight: 700;
                    letter-spacing: 0.12em;
                    background: rgba(255, 255, 255, 0.9);
                    border-radius: 999px;
                    border: 1px solid rgba(0, 159, 253, 0.2);
                    box-shadow: 0 1px 4px rgba(0, 159, 253, 0.08);
                }

                .step {
                    display: flex;
                    align-items: flex-start;
                    margin-bottom: 1.35rem;
                    padding: 1.35rem 1.35rem 1.35rem 1.15rem;
                    border-radius: 16px;
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid rgba(0, 159, 253, 0.08);
                    border-left: 5px solid #009ffd;
                    opacity: 0;
                    transform: translateX(-16px);
                    transition: all 0.35s ease;
                    box-shadow: 0 2px 16px rgba(0, 159, 253, 0.05), 0 1px 3px rgba(0, 0, 0, 0.04);
                }

                .step:hover {
                    background: #fff;
                    box-shadow: 0 6px 24px rgba(0, 159, 253, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05);
                    border-left-color: #00b8e6;
                }

                .step.animated {
                    opacity: 1;
                    transform: translateX(0);
                }

                .step-number {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: linear-gradient(145deg, #009ffd 0%, #00a8f0 50%, #00b8e6 100%);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-right: 1.35rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 16px rgba(0, 159, 253, 0.35), 0 1px 0 rgba(255, 255, 255, 0.25) inset;
                }

                .step-number span {
                    color: white;
                    font-size: 1.1rem;
                    font-weight: 700;
                    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
                }

                .step-content {
                    flex: 1;
                    min-width: 0;
                }

                .step-title {
                    color: #111;
                    font-family: "FSP DEMO - Universo Black", sans-serif;
                    font-size: 1.08rem;
                    font-weight: 600;
                    margin: 0 0 0.5rem 0;
                    line-height: 1.35;
                }

                .step-description {
                    color: #444;
                    font-size: 0.95rem;
                    line-height: 1.6;
                    margin: 0;
                }

                .learn-more-container {
                    margin-top: 2.25rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid rgba(0, 159, 253, 0.12);
                    opacity: 0;
                    transform: translateY(10px);
                    transition: all 0.35s ease;
                }

                .learn-more-container.visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                .learn-more-button {
                    width: 100%;
                    background: linear-gradient(135deg, #009ffd 0%, #00a8f0 40%, #00b8e6 70%, #00d4ff 100%);
                    border: none;
                    border-radius: 16px;
                    padding: 1.1rem 2rem;
                    color: white;
                    font-size: 1.05rem;
                    font-weight: 600;
                    cursor: pointer;
                    box-shadow: 0 4px 20px rgba(0, 159, 253, 0.3), 0 1px 0 rgba(255, 255, 255, 0.2) inset;
                    transition: all 0.3s ease;
                    letter-spacing: 0.02em;
                }

                .learn-more-button:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 36px rgba(0, 159, 253, 0.4), 0 1px 0 rgba(255, 255, 255, 0.25) inset;
                }

                .learn-more-button:active {
                    transform: translateY(0);
                }

                @media (max-width: 768px) {
                    .tutorial-container {
                        margin-top: 3.5rem;
                        padding: 1.25rem 1rem;
                    }
                    
                    .tutorial-wrapper {
                        max-width: 100%;
                    }
                    
                    .steps-container {
                        padding: 1.75rem 1.25rem;
                    }
                    
                    .button-text {
                        font-size: 1rem;
                    }
                    
                    .main-button {
                        padding: 1rem 1.5rem;
                    }
                    
                    .section-title {
                        font-size: 1.2rem;
                        margin-bottom: 1rem;
                    }
                    
                    .step {
                        padding: 1rem 1rem 1rem 0.75rem;
                    }
                    
                    .step-title {
                        font-size: 1rem;
                    }
                    
                    .step-description {
                        font-size: 0.9rem;
                    }
                }

                @media (max-width: 480px) {
                    .tutorial-container {
                        margin-top: 2.5rem;
                        padding: 1rem 0.75rem;
                    }
                    
                    .steps-container {
                        padding: 1.25rem 1rem;
                    }
                    
                    .main-button {
                        padding: 0.9rem 1.25rem;
                    }
                    
                    .button-text {
                        font-size: 0.95rem;
                    }
                    
                    .section-title {
                        font-size: 1.1rem;
                        margin-bottom: 0.75rem;
                    }
                    
                    .step {
                        margin-bottom: 1rem;
                        padding: 1rem 0.75rem 1rem 0.5rem;
                    }
                    
                    .step-number {
                        width: 32px;
                        height: 32px;
                        margin-right: 1rem;
                    }
                    
                    .step-number span {
                        font-size: 0.9rem;
                    }
                    
                    .step-title {
                        font-size: 0.95rem;
                    }
                    
                    .step-description {
                        font-size: 0.85rem;
                        line-height: 1.5;
                    }
                    
                    .learn-more-button {
                        padding: 0.9rem 1.25rem;
                        font-size: 1rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default PremsiTutorialButton;